import { useMemo, useState } from 'react';
import type { Folder, MailView } from '@jerome2/shared';
import { useStore } from '../lib/store.ts';
import { app, saveSettings } from '../state/app.ts';
import { mailStore } from '../state/mail.ts';
import { Icon } from '../ui/Icon.tsx';

// Mappenboom links: Alle mappen, favorieten, en de boom van Outlook met per map de teller van
// het actieve filter (gevlagd of ongelezen). Ingeklapte takken worden onthouden.

const OPEN_KEY = 'jerome2.folders.open';

function readOpen(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(OPEN_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}

/** Het getal achter een map: bij Concepten altijd het aantal concepten, anders ongelezen of gevlagd naar de weergave. */
export function countFor(f: Folder, view: MailView): number {
  if (f.wellKnown === 'drafts') return f.total;
  if (view === 'unread') return f.unread;
  if (view === 'flagged') return f.flagged;
  return 0;
}

/** Favorieten zijn map-ids of `wk:<naam>` voor een vaste map (zoals `wk:drafts` voor Concepten). */
export function resolveFavorites(favorites: string[], folders: Folder[]): Folder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const out: Folder[] = [];
  for (const entry of favorites) {
    const f = entry.startsWith('wk:') ? folders.find((x) => x.wellKnown === entry.slice(3)) : byId.get(entry);
    if (f && !out.some((x) => x.id === f.id)) out.push(f);
  }
  return out;
}

export function isFavorite(favorites: string[], f: Folder): boolean {
  return favorites.includes(f.id) || (!!f.wellKnown && favorites.includes(`wk:${f.wellKnown}`));
}

export function buildTree(folders: Folder[]): Map<string | null, Folder[]> {
  const byParent = new Map<string | null, Folder[]>();
  const ids = new Set(folders.map((f) => f.id));
  for (const f of folders) {
    const parent = f.parentId && ids.has(f.parentId) ? f.parentId : null;
    const list = byParent.get(parent) ?? [];
    list.push(f);
    byParent.set(parent, list);
  }
  const order = (f: Folder) => (f.wellKnown === 'inbox' ? 0 : f.wellKnown === 'drafts' ? 1 : f.wellKnown === 'sentitems' ? 2 : f.wellKnown === 'archive' ? 3 : f.wellKnown === 'deleteditems' ? 5 : f.wellKnown === 'junkemail' ? 6 : 4);
  for (const list of byParent.values()) list.sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name, 'nl'));
  return byParent;
}

/** `embedded`: in een vel op de telefoon (volle breedte, hogere regels, sterren altijd zichtbaar; de titel staat op het vel). */
export function FolderRail(props: { view: MailView; folder: string; onSelect: (folderId: string) => void; onClose?: (() => void) | undefined; embedded?: boolean | undefined }) {
  const folders = useStore(mailStore, (s) => s.folders);
  const favorites = useStore(app, (s) => s.settings.favoriteFolders);
  const [open, setOpen] = useState<Record<string, boolean>>(() => (typeof localStorage === 'undefined' ? {} : readOpen()));
  const tree = useMemo(() => buildTree(folders), [folders]);
  const touch = props.embedded === true;
  const rowH = touch ? 'h-11' : 'h-7';
  const text = touch ? 'text-[15px]' : 'text-[13px]';

  const toggle = (id: string) => {
    const next = { ...open, [id]: !(open[id] ?? true) };
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(next));
    } catch {
      // niet bewaard
    }
  };
  const toggleFavorite = (f: Folder) => {
    const wk = f.wellKnown ? `wk:${f.wellKnown}` : null;
    const next = isFavorite(favorites, f) ? favorites.filter((x) => x !== f.id && x !== wk) : [...favorites, f.id];
    void saveSettings({ favoriteFolders: next });
  };

  const renderFolder = (f: Folder, depth: number) => {
    const children = tree.get(f.id) ?? [];
    const isOpen = open[f.id] ?? true;
    const active = props.folder === f.id;
    const count = countFor(f, props.view);
    return (
      <li key={f.id}>
        <div className={`group flex items-center gap-1 rounded-md pr-1 ${active ? 'bg-tint text-ink' : 'text-ink-soft hover:bg-tint/60 hover:text-ink'}`} style={{ paddingLeft: 6 + depth * 14 }}>
          {children.length ? (
            <button type="button" aria-label={isOpen ? 'Inklappen' : 'Uitklappen'} className={`${touch ? 'h-11 w-10 -ml-2' : 'h-6 w-5'} shrink-0 flex items-center justify-center text-muted`} onClick={() => toggle(f.id)}>
              <Icon name="chevron" size={touch ? 14 : 12} className={isOpen ? 'rotate-90' : ''} />
            </button>
          ) : (
            <span className={touch ? 'w-8' : 'w-5'} />
          )}
          <button type="button" className={`flex-1 min-w-0 flex items-center gap-1.5 ${rowH} text-left`} onClick={() => props.onSelect(f.id)} aria-current={active ? 'true' : undefined} title={f.path}>
            <Icon name="folder" size={touch ? 16 : 14} className="shrink-0 opacity-70" />
            <span className={`truncate ${text} ${f.wellKnown === 'inbox' ? 'font-medium' : ''}`}>{f.name}</span>
          </button>
          {count > 0 ? (
            <span className={`${touch ? 'text-[13px]' : 'text-[12px]'} text-accent font-medium tabular-nums`} aria-label={f.wellKnown === 'drafts' ? `${count} ${count === 1 ? 'concept' : 'concepten'}` : undefined}>
              {count}
            </span>
          ) : null}
          <button type="button" aria-label={isFavorite(favorites, f) ? 'Uit favorieten' : 'Naar favorieten'} className={`${touch ? 'h-10 w-10' : 'h-6 w-6'} flex items-center justify-center rounded ${isFavorite(favorites, f) ? 'text-amber' : touch ? 'text-muted/70' : 'text-muted opacity-0 group-hover:opacity-100'}`} onClick={() => toggleFavorite(f)}>
            <Icon name="star" size={touch ? 15 : 12} />
          </button>
        </div>
        {children.length && isOpen ? <ul>{children.map((c) => renderFolder(c, depth + 1))}</ul> : null}
      </li>
    );
  };

  const favs = useMemo(() => resolveFavorites(favorites, folders), [favorites, folders]);

  return (
    <nav aria-label="Mappen" className={touch ? 'w-full h-full flex flex-col bg-surface' : 'w-[230px] shrink-0 h-full flex flex-col border-r border-line bg-paper'}>
      {touch ? null : (
        <div className="h-11 shrink-0 flex items-center px-3 text-[13px] font-medium text-ink-soft">
          Mappen
          {props.onClose ? (
            <button type="button" aria-label="Sluiten" className="ml-auto h-7 w-7 rounded flex items-center justify-center text-muted hover:bg-tint" onClick={props.onClose}>
              <Icon name="x" size={14} />
            </button>
          ) : null}
        </div>
      )}
      <div className={`flex-1 min-h-0 overflow-auto scroll-thin px-2 pb-3 ${touch ? 'pt-2' : ''}`}>
        <button type="button" className={`w-full flex items-center gap-2 ${touch ? 'h-11' : 'h-8'} px-2 rounded-md ${text} ${props.folder === 'all' ? 'bg-tint text-ink font-medium' : 'text-ink-soft hover:bg-tint/60'}`} onClick={() => props.onSelect('all')} aria-current={props.folder === 'all' ? 'true' : undefined}>
          <Icon name="mail" size={touch ? 16 : 14} /> Alle mappen
        </button>
        {favs.length ? (
          <div className="mt-3">
            <div className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted mb-1">Favorieten</div>
            <ul>{favs.map((f) => renderFolder({ ...f }, 0))}</ul>
          </div>
        ) : null}
        <div className="mt-3">
          <div className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted mb-1">Outlook</div>
          {folders.length ? <ul>{(tree.get(null) ?? []).map((f) => renderFolder(f, 0))}</ul> : <div className="px-2 text-[13px] text-muted">Mappen laden…</div>}
        </div>
      </div>
    </nav>
  );
}
